import { Card } from '@ais-ui/ui-core';
import React, {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '@ais-ui/utils';
import { useViewMode } from '../../components/ViewModeProvider';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { TargetCardContent } from './TargetCardContent';
import { TargetProvider, useTargetContext } from './TargetContext';
import { noop } from 'lodash';

interface TargetDetailsMapProps {
  isExpanded: boolean;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
  className?: string;
  screenHeight?: boolean;
}

export type TargetTab =
  | 'base'
  | 'integrity'
  | 'register'
  | 'advancedVessel'
  | 'nextPort';

interface TargetCardProps {
  isExpanded?: boolean;
  setIsExpanded?: Dispatch<SetStateAction<boolean>>;
  className?: string;
}

export const TargetCard = forwardRef<HTMLDivElement, TargetCardProps>(
  ({ isExpanded = true, setIsExpanded, className }, ref) => {
    return (
      <TargetProvider isExpanded={isExpanded} setIsExpanded={setIsExpanded}>
        <Card
          ref={ref}
          id="targetinfo-card"
          className={cn(
            'relative flex flex-col min-w-[464px] max-w-[464px] z-10 shadow-lg',
            className
          )}
          style={{ maxHeight: 'calc(100vh - 2rem)' }}
        >
          <TargetCardContent />
        </Card>
      </TargetProvider>
    );
  }
);

interface TargetCardStackProps {
  isExpanded: boolean;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
  className?: string;
}

const TargetCardStack = ({
  isExpanded,
  setIsExpanded,
  className,
}: TargetCardStackProps) => {
  const targetCardRef = useRef<HTMLDivElement>(null);
  const [targetCardHeight, setTargetCardHeight] = useState<number>(0);
  const { targetInfoDetailsList, currentTargetInfo, currentTargetType } =
    useSelectionState();

  useEffect(() => {
    if (targetCardRef.current) {
      const updateHeight = () => {
        setTargetCardHeight(targetCardRef.current!.offsetHeight);
      };

      // Initial height measurement
      updateHeight();

      // Set up ResizeObserver to watch for height changes
      const resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(targetCardRef.current);

      return () => resizeObserver.disconnect();
    }
  }, [isExpanded]);

  if (!currentTargetInfo || !currentTargetType) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative max-h-screen',
        className,
        targetInfoDetailsList.length === 2 && 'ml-3',
        targetInfoDetailsList.length >= 2 && 'ml-6'
      )}
    >
      <TargetCard
        ref={targetCardRef}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        className={cn(isExpanded ? 'h-screen' : 'h-fit')}
      />
      <div className="absolute top-0 left-0">
        {targetInfoDetailsList.length >= 3 && (
          <Card
            id="stack-card-2"
            className={cn(
              'absolute z-1 min-w-[464px] max-w-[464px] -top-3 -left-6 shadow-md cursor-default pointer-events-auto'
            )}
            style={{
              height: targetCardHeight,
              maxHeight: 'calc(100vh - 2rem)',
            }}
          />
        )}
        {targetInfoDetailsList.length >= 2 && (
          <Card
            id="stack-card-1"
            className={cn(
              'absolute z-2 min-w-[464px] max-w-[464px] -top-1.5 -left-3 shadow-md cursor-default pointer-events-auto'
            )}
            style={{
              height: targetCardHeight,
              maxHeight: 'calc(100vh - 2rem)',
            }}
          />
        )}
      </div>
    </div>
  );
};

export const TargetDetails = React.memo(
  ({ isExpanded, setIsExpanded, className }: TargetDetailsMapProps) => {
    const { targetInfoDetailsList, setTargetInfoDetailsList } =
      useSelectionState();

    const handleEscape = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setTargetInfoDetailsList([]);
        }
      },
      [setTargetInfoDetailsList]
    );

    useEffect(() => {
      window.addEventListener('keydown', handleEscape);

      return () => {
        window.removeEventListener('keydown', handleEscape);
      };
    }, [handleEscape]);

    if (targetInfoDetailsList.length === 0) {
      return null;
    }

    return (
      <TargetCardStack
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        className={className}
      />
    );
  }
);

TargetDetails.displayName = 'TargetDetails';
